import * as React from "react";

import { DataStorage } from "../../../../helpers/utils/DataStorage";
import { ModelAction } from "../../../../types/responses/ModelAction";
import { ProjectDoc } from '../../models/ProjectDoc';
import { services } from '../../../../services';
import { useBackgroundPromiseWrapper } from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { PrevIterationFileUploadComponent } from "./PrevIterationFileUploadComponent";
import { ProjectDocFolderAttachment } from '../../models/ProjectDocFolderAttachment';
import { AxiosPromise } from 'axios';
import { NoteUpdatesController } from '../../../notes/helpers/noteUpdates';


interface PrevIterationFolderFilesProps{
  projectDocFolderId: number;
  onCurrentFolderStatusUpdated: () => void;
  dataStorage: DataStorage;
  setDataStorageValue: (key: string, value: any) => void;
  projectDocFolderStatusId?: number;
  projectDoc?: ProjectDoc;
  fetchFiles?: (folderId: number) => AxiosPromise<ProjectDocFolderAttachment[]>;
  emptyText?: string;
  storageKey?: string;
  noteUpdates?: NoteUpdatesController;
}

export const PrevIterationFolderFiles: React.FC<PrevIterationFolderFilesProps> = ({ projectDocFolderId, onCurrentFolderStatusUpdated, dataStorage, setDataStorageValue, projectDocFolderStatusId, projectDoc, fetchFiles, emptyText, storageKey, noteUpdates }) => {
  const [folderAttachmentsCount, setFolderAttachmentsCount] = React.useState<number>(0);

  const [actions, setActions] = React.useState<ModelAction[]>([]);

  const [promiseWrapper] = useBackgroundPromiseWrapper();
  const dataStorageRef = React.useRef(dataStorage);
  dataStorageRef.current = dataStorage;

  const actionsRequestIdRef = React.useRef(0);
  const latestFolderIdRef = React.useRef(projectDocFolderId);

  React.useEffect(() => {
    latestFolderIdRef.current = projectDocFolderId;
  }, [projectDocFolderId]);

  const loadActions = React.useCallback((force: boolean = false) => {
    const folderId = projectDocFolderId;
    const ds = dataStorageRef.current;
    if (folderId === 0) {
      setActions([]);
      return;
    }

    const actionsKeyForFolder = `PrevIterationFolderFilesActions_actions_${folderId}`;
    if (!force && ds.hasValue(actionsKeyForFolder)) {
      setActions(ds.get<ModelAction[]>(actionsKeyForFolder, []));
      return;
    }

    const requestId = ++actionsRequestIdRef.current;
    promiseWrapper(
      () => services.projectDocFolderService.actions(folderId),
      (res: ModelAction[]) => {
        if (requestId !== actionsRequestIdRef.current) {
          return;
        }
        if (latestFolderIdRef.current !== folderId) {
          return;
        }
        ds.set(actionsKeyForFolder, res);
        setActions(res);
      }
    );
  }, [projectDocFolderId]);

  React.useEffect(() => {
    loadActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDocFolderId]);

  return (
    <React.Fragment>
      <PrevIterationFileUploadComponent
        actions={actions}
        projectDocFolderId={projectDocFolderId}
        onFilesLoaded={e => setFolderAttachmentsCount(e)}
        dataStorage={dataStorage}
        setDataStorageValue={setDataStorageValue}
        projectDocFolderStatusId={projectDocFolderStatusId}
        projectDoc={projectDoc}
        fetchFiles={fetchFiles}
        emptyText={emptyText}
        storageKey={storageKey}
        {...(noteUpdates ? { noteUpdates } : {})} />
    </React.Fragment>
  );
}
